import apiClient from './apiClient';

/**
 * Upload a file to Supabase storage
 * @param {FormData} formData - Contains file and folder
 * @returns {Promise} - File data with Location, Bucket, Key
 */
export const uploadFile = async (formData) => {
  try {
    const response = await apiClient.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Delete a file from Supabase storage
 * @param {string} fileId - File ID in database
 * @param {string} filePath - File path in Supabase storage
 * @returns {Promise}
 */
export const deleteFile = async (fileId, filePath) => {
  try {
    const response = await apiClient.delete('/files/delete', {
      data: { fileId, filePath },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Get uploaded files for a candidate
 * @param {number} companyId - Company ID
 * @returns {Promise} - Array of uploaded files
 */
export const getUploadedFiles = async (companyId) => {
  try {
    const response = await apiClient.get(`/files/uploaded`, {
      params: { companyId },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Get upload progress for a specific upload
 * @param {string} uploadId - Upload ID
 * @returns {Promise} - Upload progress percentage
 */
export const getUploadProgress = async (uploadId) => {
  try {
    const response = await apiClient.get(`/files/progress/${uploadId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Upload file directly to Supabase (client-side)
 * @param {File} file - File to upload
 * @param {string} path - Path in Supabase (e.g., 'documents', 'profile')
 * @param {string} supabaseUrl - Supabase URL
 * @param {string} anonKey - Supabase anon key
 * @returns {Promise} - Upload response with Location
 */
export const uploadToSupabaseDirectly = async (
  file,
  path = 'documents',
  supabaseUrl,
  anonKey
) => {
  try {
    const fileName = `${Date.now()}-${file.name}`;
    const filePath = `${path}/${fileName}`;
    const bucketName = 'uploads';

    const uploadUrl = `${supabaseUrl}/storage/v1/object/${bucketName}/${filePath}`;

    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${anonKey}`,
        'content-type': file.type || 'application/octet-stream',
      },
      body: file,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Upload failed');
    }

    const publicUrl = `${supabaseUrl}/storage/v1/object/public/${bucketName}/${filePath}`;

    return {
      fileName,
      filePath,
      Location: publicUrl,
      Bucket: bucketName,
      Key: filePath,
    };
  } catch (error) {
    throw error;
  }
};
